/**
 * Sparkline — recharts wrapper for the 6-week capacity/sold bar pattern
 * from `NBSpark` and the funnel/area sparklines on the Sales bucket.
 *
 * Two render modes:
 *   - "bars" — actual vs forecast columns (default). Matches NBSpark.
 *   - "area" — single tinted area for trendlines.
 */
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

export interface SparkBarPoint {
  label: string;
  sold: number;
  cap: number;
  isActual?: boolean;
}

export interface SparkLinePoint {
  label: string;
  value: number;
}

export interface SparkBarProps {
  data: SparkBarPoint[];
  height?: number;
}

export function SparkBars({ data, height = 64 }: SparkBarProps) {
  const max = Math.max(...data.map((d) => d.cap), 1);
  return (
    <div className="nb-spark" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 12 }}>
          <Bar dataKey="cap" fill="var(--nb-surface-2)" radius={3} />
          <Bar
            dataKey="sold"
            fill="var(--nb-primary)"
            radius={3}
            shape={(props) => {
              const {
                x,
                y,
                width,
                height: h,
                payload,
              } = props as {
                x: number;
                y: number;
                width: number;
                height: number;
                payload: SparkBarPoint;
              };
              if (payload.isActual === false) {
                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={h}
                    rx={3}
                    fill="transparent"
                    stroke="var(--nb-border-2)"
                    strokeDasharray="3 3"
                  />
                );
              }
              return <rect x={x} y={y} width={width} height={h} rx={3} fill="var(--nb-primary)" />;
            }}
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 9, fill: 'var(--nb-subtle)' }}
            height={14}
          />
          <YAxis hide domain={[0, max]} />
          <ReferenceLine y={max} stroke="transparent" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface SparkAreaProps {
  data: SparkLinePoint[];
  height?: number;
  tone?: 'primary' | 'warn' | 'info';
}

const TONE_FILL: Record<NonNullable<SparkAreaProps['tone']>, string> = {
  primary: 'var(--nb-primary)',
  warn: 'var(--nb-warn)',
  info: 'var(--nb-info)',
};

export function SparkArea({ data, height = 48, tone = 'primary' }: SparkAreaProps) {
  const fill = TONE_FILL[tone];
  return (
    <div className="nb-spark" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <Area
            type="monotone"
            dataKey="value"
            stroke={fill}
            fill={fill}
            fillOpacity={0.18}
            strokeWidth={1.5}
          />
          <YAxis hide />
          <XAxis dataKey="label" hide />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
